import { entityType } from 'config/entityType';
import SuperFetch from 'library/helpers/superFetch';
import _ from 'lodash';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { createAddressSaga, updateAddressSaga } from 'reduxStore/address/saga';
import { createMapSaga, updateMapSaga } from 'reduxStore/maps/saga';
import { Notification } from 'zComponents/atoms';
import { selectSiteByOrgAndSiteID } from './selectors';
import { sitesActions } from './slice';

export function* getSites() {
	try {
		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile);

		// Get sites from the current organisation.
		const sites = yield call(() => SuperFetch.get('/sites'));
		if (sites.statusCode === 200) {
			yield put(sitesActions.loadSitesSuccess({ orgID, data: sites.data }));
		} else {
			throw Error(sites.error.message || sites.error || 'Failed to load sites.');
		}
	} catch (error) {
		yield put(sitesActions.loadSitesError(error));
	}
}

export function* getSiteAddress(action) {
	try {
		//
		const { siteID } = action.payload;

		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile);

		// Get the address of the selected site.
		const address = yield call(() => SuperFetch.get(`/sites/${siteID}/address`));

		if (address.statusCode === 200) {
			yield put(sitesActions.loadSiteAddressSuccess({ orgID, siteID, data: address.data }));
		} else {
			throw Error(address.error.message || address.error || 'Failed to load address.');
		}
	} catch (error) {
		yield put(sitesActions.loadSiteAddressError(error));
	}
}

export function* createSite(action) {
	try {
		const { data } = action.payload;

		// 1. Create the site
		const createdSite = yield call(() => SuperFetch.post(`/sites`, { name: data.name }));

		if (createdSite.statusCode === 200 && createdSite.data?.siteID) {
			const siteID = createdSite.data.siteID;

			// 2. address data
			const addressData = {
				addressLine1: data.addressLine1,
				addressLine2: data.addressLine2,
				suburb: data.suburb,
				state: data.state,
				country: data.country,
				postCode: data.postCode,
				latitude: data.latitude,
				longitude: data.longitude,
				fullAddress: data.fullAddress,
				entityID: siteID,
				entityTypeID: entityType.SITE
			};

			// 3. Create address only if required fields exist
			const hasValidAddress =
				addressData.addressLine1 &&
				addressData.suburb &&
				addressData.state &&
				addressData.country &&
				addressData.postCode;

			if (hasValidAddress) {
				yield call(createAddressSaga, addressData);
			}

			// 4. map data
			const mapData = {
				name: data.name,
				dimensionX: data.dimensionX,
				dimensionY: data.dimensionY,
				note: data.note,
				url: data.url,
				entityID: siteID,
				entityTypeID: entityType.SITE
			};

			// 5. Create map only if required fields exist
			const hasValidMap = mapData.name && mapData.dimensionX && mapData.dimensionY;

			if (hasValidMap) {
				yield call(createMapSaga, mapData);
			}

			Notification('success', 'Site created successfully.');
			yield put(sitesActions.createSiteSuccess());
		} else {
			throw Error(createdSite.error?.message || 'Failed to create site.');
		}
	} catch (error) {
		yield put(sitesActions.createSiteError(error));
		Notification('error', error.message || 'Failed to create site.');
	}
}

export function* afterCreateSiteSuccess() {
	yield put(sitesActions.loadSites()); // <- Refresh sites list
}

export function* updateSite(action) {
	try {
		const { siteID, data } = action.payload;

		// Get the previous site data to compare changes
		const previousData = yield select(selectSiteByOrgAndSiteID, siteID);

		// Site Payload
		const siteData = {
			name: data.name
		};

		// Address Payload
		const addressData = {
			addressLine1: data.addressLine1,
			addressLine2: data.addressLine2,
			suburb: data.suburb,
			state: data.state,
			country: data.country,
			postCode: data.postCode,
			latitude: data.latitude,
			longitude: data.longitude,
			fullAddress: data.fullAddress,
			entityID: siteID,
			entityTypeID: entityType.SITE
		};

		// Map Payload
		const mapData = {
			name: data.name,
			dimensionX: data.dimensionX,
			dimensionY: data.dimensionY,
			note: data.note,
			url: data.url,
			entityID: siteID,
			entityTypeID: entityType.SITE
		};

		// 1. Check if site data has changed
		const siteChanged = !_.isEqual(_.pick(data, ['name']), _.pick(previousData, ['name']));

		// 2. If site data has changed, update the site
		if (siteChanged) {
			const updatedSite = yield call(() => SuperFetch.patch(`/sites/${siteID}`, siteData));
			if (updatedSite.statusCode !== 200) throw Error(updatedSite.error?.message || 'Failed to update site.');
		}

		// 3. Check if address data has changed
		const addressChanged = !_.isEqual(
			_.pick(data, [
				'addressLine1',
				'addressLine2',
				'suburb',
				'state',
				'country',
				'postCode',
				'latitude',
				'longitude',
				'fullAddress'
			]),
			_.pick(previousData, [
				'addressLine1',
				'addressLine2',
				'suburb',
				'state',
				'country',
				'postCode',
				'latitude',
				'longitude',
				'fullAddress'
			])
		);

		// 4. If the site already has an addressID, update it; otherwise, create a
		if (addressChanged) {
			if (data.addressID) {
				yield call(updateAddressSaga, data.addressID, addressData);
			} else if (
				addressData.addressLine1 &&
				addressData.suburb &&
				addressData.state &&
				addressData.country &&
				addressData.postCode
			) {
				yield call(createAddressSaga, addressData);
			}
		}

		// 5. Check if map data has changed
		const mapChanged = !_.isEqual(
			_.pick(data, ['dimensionX', 'dimensionY', 'note', 'url']),
			_.pick(previousData, ['dimensionX', 'dimensionY', 'note', 'url'])
		);

		// 6. If the site already has a mapID, update it; otherwise, create a new map
		if (mapChanged) {
			if (data.mapID) {
				yield call(updateMapSaga, data.mapID, mapData);
			} else if (mapData.name && mapData.dimensionX && mapData.dimensionY) {
				yield call(createMapSaga, mapData);
			}
		}

		Notification('success', 'Site updated successfully.');
		yield put(sitesActions.updateSiteSuccess());
	} catch (error) {
		yield put(sitesActions.updateSiteError(error));
		Notification('error', error.message || 'Failed to update site.');
	}
}

export function* afterUpdateSiteSuccess() {
	yield put(sitesActions.loadSites());
}

export function* deleteSite(action) {
	try {
		const { siteID } = action.payload;

		// Delete site from the org.
		const sites = yield call(() => SuperFetch.delete(`/sites/${siteID}`));

		if (sites.statusCode === 200) {
			Notification('success', 'Site deleted successfully.');
			yield put(sitesActions.deleteSiteSuccess());
		} else {
			throw Error(sites.error?.message || sites.error || 'Failed to delete a site.');
		}
	} catch (error) {
		yield put(sitesActions.deleteSiteError(error));
		Notification('error', error.message || 'Failed to delete site.');
	}
}

export function* afterDeleteSiteSuccess() {
	yield put(sitesActions.loadSites());
}

export function* getSiteMaps({ payload }) {
	try {
		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile);

		// Get the siteID from the payload
		const { siteID } = payload;

		// Get the map of the selected site.
		const map = yield call(() => SuperFetch.get(`/sites/${siteID}/maps`));

		if (map.statusCode === 200) {
			yield put(sitesActions.loadSiteMapSuccess({ orgID, siteID, data: map.data }));
		} else {
			throw Error(map.error.message || map.error || 'Failed to load map.');
		}
	} catch (error) {
		yield put(sitesActions.loadSiteMapError(error));
	}
}

export default function* rootSaga() {
	yield takeLatest(sitesActions.loadSites, getSites);
	yield takeLatest(sitesActions.loadSiteAddress, getSiteAddress);
	yield takeLatest(sitesActions.createSite, createSite);
	yield takeLatest(sitesActions.createSiteSuccess, afterCreateSiteSuccess);
	yield takeLatest(sitesActions.updateSite, updateSite);
	yield takeLatest(sitesActions.updateSiteSuccess, afterUpdateSiteSuccess);
	yield takeLatest(sitesActions.deleteSite, deleteSite);
	yield takeLatest(sitesActions.deleteSiteSuccess, afterDeleteSiteSuccess);
	yield takeLatest(sitesActions.loadSiteMap, getSiteMaps);
}
